// ─────────────────────────────────────────────────────────────────────────────
// Promo-code redemption glue shared by every signup surface (email +
// Apple + Google).
//
// Flow at signup:
//   1. User types a code in the "Have a code?" field before hitting
//      Sign in with Apple / Google / Create account.
//   2. `storePendingPromoCode()` writes it to sessionStorage — survives
//      the OAuth round-trip (Google) and page reloads.
//   3. As soon as the app sees a fresh `user` via useAuth, the App.js
//      post-auth effect calls `redeemPendingPromoCode()`, which posts to
//      /api/promo/redeem. On success the user is now VIP.
//   4. sessionStorage entry is cleared regardless of success/failure so
//      the next signup surface doesn't inherit a stale code.
// ─────────────────────────────────────────────────────────────────────────────

import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "bc_pending_promo_code";

export const storePendingPromoCode = (code) => {
  const c = (code || "").trim().toUpperCase();
  try {
    if (c) sessionStorage.setItem(KEY, c);
    else sessionStorage.removeItem(KEY);
  } catch { /* ignore quota errors */ }
};

export const readPendingPromoCode = () => {
  try { return sessionStorage.getItem(KEY) || ""; } catch { return ""; }
};

export const clearPendingPromoCode = () => {
  try { sessionStorage.removeItem(KEY); } catch { /* ignore */ }
};

/**
 * Attempt to redeem the pending code. Resolves to:
 *   { ok: true, code, remaining }
 *   { ok: false, reason: "promo_full" | "already_vip" | "unknown_promo_code" | "existing_subscriber" | "network" }
 * The frontend can toast the user based on the reason without needing to
 * parse HTTP status codes.
 */
export const redeemPendingPromoCode = async (getAuthHeader) => {
  const code = readPendingPromoCode();
  if (!code) return { ok: false, reason: "no_code" };
  try {
    const headers = getAuthHeader ? await getAuthHeader() : {};
    const res = await axios.post(
      `${API}/promo/redeem`,
      { code },
      { headers, withCredentials: true }
    );
    clearPendingPromoCode();
    return { ok: true, ...res.data };
  } catch (err) {
    clearPendingPromoCode();
    const detail = err?.response?.data?.detail || "";
    return { ok: false, reason: detail || "network" };
  }
};
